import { useState } from "react";
import { Form } from "@/components/shared/forms/Form";
import { useForm, SubmitHandler } from "react-hook-form";
import { useParams, useRouteContext } from "@tanstack/react-router";
import {
  type GridRowClickEvent,
  type GridColumnProps,
} from "@progress/kendo-react-grid";

import { type SCLR, SCLR_TEMPLATE } from "@/models";
import { sclrMutationFunctions } from "@/models/stff";
import { Parse_CLRM_Key } from "@/models/clrm.tsx";
import { useLeaData } from "@/queries.ts";
import { type ComboBoxOption } from "@/components/shared/forms/SearchComboBox";
import KendoTableClientState from "@/components/shared/tables/KendoTableClientState";
import TooltipTrigger from "@/components/shared/TooltipTrigger";
import Popup from "@/components/shared/Popup";
import SCLRAddEditForm from "../../staff/SCLRAddEditForm";
import { FiEye, FiPlus, FiSave, FiTrash2 } from "react-icons/fi";
import {
  createNamedFilename,
  CSVDownloadButton,
} from "@/components/shared/CSVDownloadButton";
import { useRecordSubmission } from "@/hooks/useRecordSubmission";
import LoaderButton from "@/components/shared/LoaderButton";
import { DeleteConfirmModal } from "@/components/shared/DeleteConfirmModal";

const columns: GridColumnProps[] = [
  {
    id: "ProgramYear",
    field: "ProgramYear",
    title: "Program Year",
  },
  {
    id: "PreschoolName",
    field: "PreschoolName",
    title: "School",
  },
  {
    id: "LocalClassroomId",
    field: "LocalClassroomId",
    title: "Classroom ID",
  },
  {
    id: "StaffName",
    field: "StaffName",
    title: "Assigned Staff",
  },
];

const csvHeaders = [
  { label: "Program Year", key: "ProgramYear" },
  { label: "School", key: "PreschoolName" },
  { label: "Classroom ID", key: "LocalClassroomId" },
  { label: "Assigned Staff", key: "StaffName" },
];

export default function SCLRRecordsTab({
  sclrRecords,
  classroomName,
}: {
  sclrRecords: SCLR[];
  classroomName?: string;
}) {
  const params = useParams({
    shouldThrow: false,
    from: "/$leaid/_error-wrapper/records/classroom/$entityid",
  });
  const classroomRef =
    params?.entityid === undefined ? {} : Parse_CLRM_Key(params.entityid);

  const leaContext = useRouteContext({ from: "/$leaid" });
  const [popupOpen, setPopupOpen] = useState<boolean>(false);
  const [popupStaffClassroom, setPopupStaffClassroom] = useState<
    SCLR | undefined
  >(undefined);
  const [sclrDefaults, setSclrDefaults] = useState<Partial<SCLR> | undefined>(
    undefined
  );
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);

  const addEditForm = useForm<SCLR>({
    defaultValues: {},
  });

  const result = useLeaData(leaContext.currentUserOrg.esiIdPath);
  const leaData = result.data;

  const clrmRecords = params?.entityid
    ? (leaData?.entities?.classrooms?.[params.entityid]?.records?.map(
        (record) => ({
          ...record,
          PreschoolName: leaContext?.currentOrgPreschools?.find(
            (x) => x.PreschoolCDSCode === record.PreschoolCode
          )?.PreschoolName,
        })
      ) ?? [])
    : [];

  const sortedClrmRecords = [...clrmRecords].sort((x, y) =>
    y.ProgramYear.localeCompare(x.ProgramYear)
  );
  const currentClassroom = sortedClrmRecords[0];

  const staffOptions: ComboBoxOption[] = (() => {
    if (!leaData?.stff || !leaData?.sass || !currentClassroom) return [];

    const matchingSassStaffIds = new Set(
      leaData.sass
        .filter(
          (sass) =>
            sass.PreschoolCode === currentClassroom.PreschoolCode &&
            sass.ProgramYear === currentClassroom.ProgramYear
        )
        .map((sass) => sass.LocalStaffId)
    );

    return leaData.stff
      .filter(
        (staff) =>
          staff.LocalStaffId &&
          matchingSassStaffIds.has(staff.LocalStaffId) &&
          !staff.EffectiveEndDate
      )
      .map((staff) => ({
        value: staff.LocalStaffId,
        label: [staff.StaffLegalFirstName, staff.StaffLegalLastName]
          .filter(Boolean)
          .join(" "),
        sublabel: `ID: ${staff.LocalStaffId}`,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  })();

  const closePopup = () => {
    setPopupOpen(false);
    setPopupStaffClassroom(undefined);
    setSclrDefaults(undefined);
    setIsDeleting(false);
    setShowDeleteConfirm(false);
    addEditForm.reset({});
    addEditForm.clearErrors();
  };

  const { isSubmitting, setSubmitResult, handleSubmitStart } =
    useRecordSubmission({
      form: addEditForm,
      recordType: "SCLR",
      toastMessage:
        "Staff classroom record submitted.\nWaiting for validation...",
      onSuccess: closePopup,
    });

  const {
    isSubmitting: isDeleteSubmitting,
    setSubmitResult: setDeleteSubmitResult,
    handleSubmitStart: handleDeleteStart,
  } = useRecordSubmission({
    form: addEditForm,
    recordType: "SCLR",
    toastMessage: "Staff classroom record submitted for deletion",
    successMessage: "Staff classroom record deleted successfully",
    onSuccess: closePopup,
  });

  const mutateFns = sclrMutationFunctions(leaData, setSubmitResult);
  const deleteMutateFns = sclrMutationFunctions(leaData, setDeleteSubmitResult);

  const onSubmit: SubmitHandler<SCLR> = (x) => {
    handleSubmitStart();
    mutateFns.handleSave(x);
  };

  const onDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = () => {
    setShowDeleteConfirm(false);
    if (popupStaffClassroom) {
      setIsDeleting(true);
      handleDeleteStart();
      deleteMutateFns.handleSave(popupStaffClassroom, true);
    }
  };

  const onAddButtonClick = () => {
    const defaults: Partial<SCLR> = {
      ...classroomRef,
      PreschoolCode: currentClassroom?.PreschoolCode ?? "",
      ProgramYear: currentClassroom?.ProgramYear ?? "",
      LocalClassroomId: currentClassroom?.LocalClassroomId ?? "",
    };

    setSclrDefaults(defaults);
    setPopupStaffClassroom(undefined);
    setPopupOpen(true);

    addEditForm.reset({ ...SCLR_TEMPLATE, ...defaults });
    addEditForm.clearErrors();
  };

  const onEditButtonClick = (c: SCLR) => {
    setSclrDefaults(undefined);
    setPopupStaffClassroom(c);
    setPopupOpen(true);

    addEditForm.reset(c);
    addEditForm.clearErrors();
  };

  const formId = "frmSCLR";
  const canEdit = leaContext.hasRole("SCLREdit");
  const isExistingRecord = popupStaffClassroom != null;

  const tableData = sclrRecords.map((record) => ({
    ...record,
    ClassroomName: classroomName ?? "",
  }));

  return (
    <>
      <div className="d-flex justify-content-end align-items-center mb-3">
        <TooltipTrigger tooltipText="Export staff classroom records">
          <CSVDownloadButton
            data={tableData}
            headers={csvHeaders}
            filename={createNamedFilename(
              ["ClassroomName"],
              "staff-classroom-record"
            )}
          />
        </TooltipTrigger>
        {canEdit && (
          <TooltipTrigger tooltipText="Add a staff classroom record">
            <button
              type="button"
              className="btn btn-primary"
              onClick={onAddButtonClick}
            >
              <FiPlus className="me-1" />
              Add
            </button>
          </TooltipTrigger>
        )}
      </div>
      <KendoTableClientState
        data={tableData}
        columns={columns}
        onRowClick={(event: GridRowClickEvent) => {
          onEditButtonClick(event.dataItem);
        }}
      />
      <Popup
        show={popupOpen}
        enforceFocus={!showDeleteConfirm}
        header={
          isExistingRecord ? (
            <div
              className="d-flex align-items-center gap-2"
              style={{ fontSize: "1.25rem" }}
            >
              <FiEye size={20} />
              <span>View Staff Classroom Record</span>
            </div>
          ) : (
            <div
              className="d-flex align-items-center gap-2"
              style={{ fontSize: "1.25rem" }}
            >
              {canEdit ? <FiPlus size={20} /> : <FiEye size={20} />}
              <span>
                {canEdit
                  ? "Add Staff Classroom Record"
                  : "View Staff Classroom Record"}
              </span>
            </div>
          )
        }
        hasButton={false}
        submitFormId={canEdit && !isExistingRecord ? formId : undefined}
        submitButtonText={
          <span className="d-flex align-items-center">
            <FiSave className="me-1" />
            Save
          </span>
        }
        submitButtonLoading={isSubmitting}
        submitButtonLoadingText="Submitting..."
        secondButtonText="Cancel"
        size="lg"
        onClose={closePopup}
        onSecondButtonClick={closePopup}
        canEdit={canEdit && !isExistingRecord}
        leftFooterButton={
          canEdit && isExistingRecord ? (
            <LoaderButton
              variant="danger"
              type="button"
              onClick={onDeleteClick}
              loading={isDeleting && isDeleteSubmitting}
              loadingText="Deleting..."
              className="d-flex align-items-center"
            >
              <FiTrash2 className="me-1" />
              Delete
            </LoaderButton>
          ) : undefined
        }
      >
        <Form id={formId} onSubmit={addEditForm.handleSubmit(onSubmit)}>
          <SCLRAddEditForm
            staffClassroom={popupStaffClassroom}
            form={addEditForm}
            sclrDefaults={sclrDefaults}
            staffOptions={staffOptions}
          />
        </Form>
      </Popup>

      {showDeleteConfirm && (
        <DeleteConfirmModal
          onCancel={() => setShowDeleteConfirm(false)}
          onConfirm={handleConfirmDelete}
        />
      )}
    </>
  );
}
